import type {
  DeliveryProblemKind,
  DeliveryStatus,
  DriverStopView,
  RecordDeliveryInput,
} from "@kitchen/schemas";
import { Button, Chip, faDigits, Field, Icon, Panel } from "@kitchen/ui";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { distributionApi, type DriverDispatchRow } from "../../api.js";
import { tehranToday } from "../../labels.js";
import { ErrorLine, useAction } from "../common.js";

const STOP_STATUS_LABELS: Record<string, { label: string; tone?: "ok" | "coral" | "warn" }> = {
  pending: { label: "در انتظار تحویل" },
  "on-route": { label: "در مسیر", tone: "warn" },
  delivered: { label: "تحویل کامل", tone: "ok" },
  "delivered-short": { label: "تحویل با کسری", tone: "warn" },
  failed: { label: "عدم تحویل", tone: "coral" },
};

export function DriverPage() {
  const today = tehranToday();
  const dispatches = useQuery({
    queryKey: ["distribution", "driver", today],
    queryFn: () => distributionApi.getDriverDispatch(today),
    refetchInterval: 15_000,
  });

  const [activeStopTarget, setActiveStopTarget] = useState<{
    dispatch: DriverDispatchRow;
    stop: DriverStopView;
  } | null>(null);

  const [showProblemReport, setShowProblemReport] = useState<DriverDispatchRow | null>(null);

  const confirmLoading = useAction(
    async ({ dispatchId, version }: { dispatchId: string; version: number }) => {
      await distributionApi.driverConfirmLoading(dispatchId, version);
    },
    [["distribution", "driver"]],
  );

  return (
    <div
      style={{
        maxWidth: 480,
        margin: "0 auto",
        padding: "var(--s3)",
        display: "flex",
        flexDirection: "column",
        gap: "var(--s3)",
      }}
    >
      <header className="row-between" style={{ alignItems: "center" }}>
        <div>
          <h1 className="t-title" style={{ margin: 0 }}>
            توزیع و تحویل راننده
          </h1>
          <div className="muted" style={{ fontSize: 13 }}>
            امروز: {today}
          </div>
        </div>
        {dispatches.data && dispatches.data[0] && (
          <Button
            variant="ghost"
            icon="alert"
            onClick={() => {
              const first = dispatches.data[0];
              if (first) {
                setShowProblemReport(first);
              }
            }}
            style={{ minHeight: 44 }}
          >
            گزارش مشکل مسیر
          </Button>
        )}
      </header>

      {dispatches.isLoading && <p className="muted">در حال دریافت سفرهای امروز...</p>}

      {dispatches.data && dispatches.data.length === 0 && (
        <div className="card" style={{ padding: "var(--s4)", textAlign: "center" }}>
          <p className="ink2" style={{ fontSize: 16 }}>
            هیچ سفری برای شما برای تاریخ امروز ثبت نشده است.
          </p>
        </div>
      )}

      {dispatches.data &&
        dispatches.data.map((dispatch) => {
          const isPlanned = !dispatch.startedAt;
          const stops = [...dispatch.stops].sort((a, b) => a.sequence - b.sequence);
          const isCompleted = dispatch.stops.every(
            (s) => s.status === "delivered" || s.status === "delivered-short" || s.status === "failed",
          );

          return (
            <div key={dispatch.id} className="stack" style={{ gap: "var(--s3)" }}>
              {/* Route Summary Card */}
              <div
                className="card"
                style={{
                  padding: "var(--s3) var(--s4)",
                  background: "var(--panel)",
                  display: "flex",
                  flexDirection: "column",
                  gap: "var(--s2)",
                }}
              >
                <div className="row-between" style={{ alignItems: "center" }}>
                  <span className="b" style={{ fontSize: 18 }}>
                    سفر توزیع امروز
                  </span>
                  <Chip tone={isCompleted ? "ok" : isPlanned ? undefined : "warn"}>
                    {isCompleted ? "پایان سفر" : isPlanned ? "آماده بارگیری" : "در مسیر توزیع"}
                  </Chip>
                </div>

                {dispatch.vehicleNote && (
                  <div className="muted" style={{ fontSize: 13 }}>
                    خودرو: {dispatch.vehicleNote}
                  </div>
                )}

                {/* Big Loading Confirmation Button */}
                {isPlanned && (
                  <div style={{ marginTop: "var(--s2)" }}>
                    <button
                      type="button"
                      className="btn btn-primary btn-lg btn-block"
                      style={{
                        minHeight: 56,
                        fontSize: 18,
                        fontWeight: 700,
                        boxShadow: "var(--sh-raised)",
                      }}
                      disabled={confirmLoading.busy}
                      onClick={() => {
                        confirmLoading.run({ dispatchId: dispatch.id, version: dispatch.version ?? 0 });
                      }}
                    >
                      {confirmLoading.busy ? "در حال ثبت..." : "بارگیری شد"}
                    </button>
                  </div>
                )}
              </div>

              {/* Stops in Order */}
              <div className="stack" style={{ gap: "var(--s2)" }}>
                <div className="b muted" style={{ fontSize: 14 }}>
                  ایستگاه‌های توزیع ({faDigits(stops.length)} ایستگاه):
                </div>

                {stops.map((stop) => {
                  const st = STOP_STATUS_LABELS[stop.status] ?? { label: stop.status };
                  const isDone =
                    stop.status === "delivered" ||
                    stop.status === "delivered-short" ||
                    stop.status === "failed";

                  return (
                    <div
                      key={stop.id}
                      className="card"
                      style={{
                        padding: "var(--s3) var(--s4)",
                        display: "flex",
                        flexDirection: "column",
                        gap: "var(--s2)",
                        borderInlineStart: isDone
                          ? stop.status === "delivered"
                            ? "4px solid var(--ok)"
                            : "4px solid var(--coral)"
                          : "4px solid var(--line)",
                      }}
                    >
                      <div className="row-between" style={{ alignItems: "center" }}>
                        <div className="row" style={{ gap: "var(--s2)", alignItems: "center" }}>
                          <span
                            className="num"
                            style={{
                              width: 28,
                              height: 28,
                              borderRadius: "50%",
                              background: "var(--panel)",
                              display: "grid",
                              placeItems: "center",
                              fontWeight: 700,
                              fontSize: 14,
                            }}
                          >
                            {faDigits(stop.sequence)}
                          </span>
                          <span className="b" style={{ fontSize: 16 }}>
                            {stop.unitName}
                          </span>
                        </div>
                        <Chip tone={st.tone}>{st.label}</Chip>
                      </div>

                      {/* Contact phone and address */}
                      {stop.unitAddress && (
                        <div className="muted" style={{ fontSize: 13 }}>
                          آدرس: {stop.unitAddress}
                        </div>
                      )}

                      {stop.contactPhone && (
                        <div className="row" style={{ gap: "var(--s2)", alignItems: "center" }}>
                          <a
                            href={`tel:${stop.contactPhone}`}
                            className="btn btn-ghost"
                            style={{
                              minHeight: 48,
                              fontSize: 15,
                              padding: "0 var(--s3)",
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 6,
                            }}
                          >
                            <Icon name="phone" />
                            تماس با تحویل‌گیرنده ({stop.contactPersonName ?? stop.contactPhone})
                          </a>
                        </div>
                      )}

                      {/* Items summary (NO special portions, NO prices) */}
                      <div
                        style={{
                          background: "var(--panel)",
                          padding: "var(--s2) var(--s3)",
                          borderRadius: "var(--r-control)",
                          fontSize: 13,
                        }}
                      >
                        {stop.items.map((item) => (
                          <div key={item.orderLineId} className="row-between" style={{ padding: "2px 0" }}>
                            <span>{item.foodName}</span>
                            <span className="num b">
                              {item.deliveredQuantity !== undefined
                                ? `${faDigits(item.deliveredQuantity)} از ${faDigits(item.orderedQuantity)} پرس`
                                : `${faDigits(item.orderedQuantity)} پرس`}
                            </span>
                          </div>
                        ))}
                      </div>

                      {stop.deliveryResult?.failureReason && (
                        <div style={{ color: "var(--coral-ink)", fontSize: 13 }}>
                          علت عدم تحویل: {stop.deliveryResult.failureReason}
                        </div>
                      )}

                      {/* Action button: Big touch target >= 48px */}
                      <button
                        type="button"
                        className={`btn ${isDone ? "btn-ghost" : "btn-primary"} btn-block`}
                        style={{
                          minHeight: 48,
                          fontSize: 16,
                          fontWeight: 700,
                          marginTop: 4,
                        }}
                        onClick={() => {
                          setActiveStopTarget({ dispatch, stop });
                        }}
                      >
                        {isDone ? "ویرایش ثبت تحویل" : "ثبت تحویل این ایستگاه"}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}

      {/* Delivery Recording Modal */}
      {activeStopTarget && (
        <DeliveryModal
          dispatch={activeStopTarget.dispatch}
          stop={activeStopTarget.stop}
          onClose={() => {
            setActiveStopTarget(null);
          }}
        />
      )}

      {/* Problem Report Modal */}
      {showProblemReport && (
        <ProblemReportModal
          dispatch={showProblemReport}
          onClose={() => {
            setShowProblemReport(null);
          }}
        />
      )}
    </div>
  );
}

function DeliveryModal({
  dispatch,
  stop,
  onClose,
}: {
  dispatch: DriverDispatchRow;
  stop: DriverStopView;
  onClose: () => void;
}) {
  const initialStatus: DeliveryStatus =
    stop.status === "delivered-short" ? "delivered-short" : stop.status === "failed" ? "failed" : "delivered";

  const [status, setStatus] = useState<DeliveryStatus>(initialStatus);
  const [failureReason, setFailureReason] = useState(stop.deliveryResult?.failureReason ?? "");
  const [containersDelivered, setContainersDelivered] = useState(
    Object.values(stop.deliveryResult?.containersDelivered ?? {}).reduce((a, b) => a + b, 0),
  );
  const [containersReturned, setContainersReturned] = useState(
    Object.values(stop.deliveryResult?.containersReturned ?? {}).reduce((a, b) => a + b, 0),
  );
  const [complaintNote, setComplaintNote] = useState(stop.deliveryResult?.complaintNote ?? "");

  // Quantities per line (default to planned quantity or previously recorded delivered quantity)
  const [quantities, setQuantities] = useState<Record<string, number>>(() => {
    const map: Record<string, number> = {};
    for (const item of stop.items) {
      map[item.orderLineId] = item.deliveredQuantity ?? item.orderedQuantity;
    }
    return map;
  });

  // Automatically update status to delivered-short if counts are less than ordered
  useEffect(() => {
    if (status === "failed") return;
    const isShort = stop.items.some((item) => (quantities[item.orderLineId] ?? 0) < item.orderedQuantity);
    if (isShort && status === "delivered") {
      setStatus("delivered-short");
    }
  }, [quantities, status, stop.items]);

  const submit = useAction(async () => {
    const payload: RecordDeliveryInput = {
      status,
      deliveredCounts: stop.items.map((item) => ({
        orderLineId: item.orderLineId,
        deliveredQuantity: status === "failed" ? 0 : Math.max(0, quantities[item.orderLineId] ?? 0),
      })),
      ...(status !== "delivered" && failureReason.trim() ? { failureReason: failureReason.trim() } : {}),
      ...(complaintNote.trim() ? { complaintNote: complaintNote.trim() } : {}),
      containersDelivered: containersDelivered > 0 ? { default: containersDelivered } : undefined,
      containersReturned: containersReturned > 0 ? { default: containersReturned } : undefined,
    };

    await distributionApi.recordStopDelivery(dispatch.id, stop.id, dispatch.version ?? 0, payload);
    onClose();
  }, [["distribution", "driver"]]);

  return (
    <Panel
      title={`ثبت تحویل: ${stop.unitName}`}
      actions={
        <Button variant="ghost" onClick={onClose}>
          بستن
        </Button>
      }
    >
      <form
        className="stack"
        style={{ gap: "var(--s4)" }}
        onSubmit={(e) => {
          e.preventDefault();
          submit.run(undefined);
        }}
      >
        {/* Status Selector: Big touch buttons */}
        <div className="stack" style={{ gap: "var(--s1)" }}>
          <label className="b" style={{ fontSize: 14 }}>
            وضعیت کلی تحویل:
          </label>
          <div className="row wrap" style={{ gap: "var(--s2)" }}>
            <button
              type="button"
              className={`btn ${status === "delivered" ? "btn-primary" : "btn-ghost"}`}
              style={{
                flex: "1 1 100px",
                minHeight: 48,
                fontSize: 15,
                fontWeight: 700,
              }}
              onClick={() => {
                setStatus("delivered");
              }}
            >
              تحویل کامل
            </button>
            <button
              type="button"
              className={`btn ${status === "delivered-short" ? "btn-primary" : "btn-ghost"}`}
              style={{
                flex: "1 1 100px",
                minHeight: 48,
                fontSize: 15,
                fontWeight: 700,
                color: status === "delivered-short" ? undefined : "var(--warn-ink)",
              }}
              onClick={() => {
                setStatus("delivered-short");
              }}
            >
              تحویل با کسری
            </button>
            <button
              type="button"
              className={`btn ${status === "failed" ? "btn-urgent" : "btn-ghost"}`}
              style={{
                flex: "1 1 100px",
                minHeight: 48,
                fontSize: 15,
                fontWeight: 700,
                color: status === "failed" ? undefined : "var(--coral-ink)",
              }}
              onClick={() => {
                setStatus("failed");
              }}
            >
              عدم تحویل
            </button>
          </div>
        </div>

        {/* If short or failed, ask for reason */}
        {status !== "delivered" && (
          <div className="field">
            <label htmlFor="failure-reason">علت کسری یا عدم تحویل</label>
            <select
              id="failure-reason"
              className="input"
              value={failureReason}
              onChange={(e) => {
                setFailureReason(e.target.value);
              }}
              style={{ minHeight: 48, fontSize: 15 }}
              required
            >
              <option value="">انتخاب علت...</option>
              <option value="واحد تعطیل بود">واحد تعطیل بود</option>
              <option value="عدم حضور تحویل‌گیرنده">عدم حضور تحویل‌گیرنده</option>
              <option value="کسری غذا در بارگیری">کسری غذا در بارگیری</option>
              <option value="خرابی غذا در مسیر">خرابی غذا در مسیر</option>
              <option value="رد سفارش توسط مشتری">رد سفارش توسط مشتری</option>
              <option value="آدرس اشتباه">آدرس اشتباه</option>
              <option value="سایر">سایر</option>
            </select>
          </div>
        )}

        {/* Portions per dish line */}
        {status !== "failed" && (
          <div className="stack" style={{ gap: "var(--s3)" }}>
            <div className="b" style={{ fontSize: 14 }}>
              تعداد پرس تحویلی به تفکیک غذا:
            </div>
            {stop.items.map((item) => {
              const currentVal = quantities[item.orderLineId] ?? item.orderedQuantity;
              return (
                <div
                  key={item.orderLineId}
                  className="row-between"
                  style={{
                    alignItems: "center",
                    padding: "var(--s2) 0",
                    borderBottom: "1px solid var(--card-line)",
                  }}
                >
                  <div>
                    <div className="b" style={{ fontSize: 15 }}>
                      {item.foodName}
                    </div>
                    <div className="muted" style={{ fontSize: 12 }}>
                      برنامه‌ریزی: {faDigits(item.orderedQuantity)} پرس
                    </div>
                  </div>
                  <NumberStepper
                    label={item.foodName}
                    value={currentVal}
                    onChange={(n) => {
                      setQuantities({ ...quantities, [item.orderLineId]: n });
                    }}
                  />
                </div>
              );
            })}
          </div>
        )}

        {/* Returnable Containers */}
        <div className="card" style={{ padding: "var(--s3)", background: "var(--panel)" }}>
          <div className="b" style={{ marginBottom: "var(--s2)", fontSize: 14 }}>
            ظروف امانی (تعداد):
          </div>
          <div className="row-between" style={{ alignItems: "center", marginBottom: "var(--s2)" }}>
            <span>ظروف تحویلی به واحد (رفت):</span>
            <NumberStepper
              label="ظروف تحویلی"
              value={containersDelivered}
              onChange={setContainersDelivered}
            />
          </div>
          <div className="row-between" style={{ alignItems: "center" }}>
            <span>ظروف مسترد شده (برگشت):</span>
            <NumberStepper label="ظروف برگشتی" value={containersReturned} onChange={setContainersReturned} />
          </div>
        </div>

        {/* Complaint / Note */}
        <Field
          id="deliv-note"
          label="یادداشت یا توضیحات تحویل (اختیاری)"
          value={complaintNote}
          onChange={(e) => {
            setComplaintNote(e.target.value);
          }}
          placeholder="مثلاً: تحویل به نگهبانی شد"
        />

        <ErrorLine error={submit.error} />

        {/* Big Submit Button */}
        <div className="row" style={{ gap: "var(--s2)", marginTop: "var(--s2)" }}>
          <Button variant="ghost" onClick={onClose} disabled={submit.busy} style={{ minHeight: 52 }}>
            انصراف
          </Button>
          <button
            type="submit"
            className="btn btn-primary btn-block"
            disabled={submit.busy || (status !== "delivered" && !failureReason.trim())}
            style={{
              minHeight: 52,
              fontSize: 18,
              fontWeight: 700,
            }}
          >
            {submit.busy ? "در حال ثبت..." : "تأیید و ثبت تحویل"}
          </button>
        </div>
      </form>
    </Panel>
  );
}

function NumberStepper({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number;
  onChange: (n: number) => void;
}) {
  return (
    <div className="row" style={{ alignItems: "center", gap: "var(--s2)" }}>
      <button
        type="button"
        className="btn"
        style={{
          width: 48,
          height: 48,
          fontSize: 22,
          padding: 0,
          display: "grid",
          placeItems: "center",
        }}
        aria-label={`کم کردن ${label}`}
        onClick={() => {
          onChange(Math.max(0, value - 1));
        }}
      >
        <Icon name="minus" />
      </button>

      <input
        type="text"
        inputMode="numeric"
        className="input num"
        style={{
          width: 60,
          height: 48,
          textAlign: "center",
          fontSize: 18,
          fontWeight: 700,
        }}
        aria-label={`تعداد ${label}`}
        value={faDigits(value)}
        onChange={(e) => {
          const raw = e.target.value
            .replace(/[۰-۹]/g, (d) => String("۰۱۲۳۴۵۶۷۸۹".indexOf(d)))
            .replace(/\D/g, "");
          const n = raw ? parseInt(raw, 10) : 0;
          onChange(Math.min(10_000, n));
        }}
      />

      <button
        type="button"
        className="btn"
        style={{
          width: 48,
          height: 48,
          fontSize: 22,
          padding: 0,
          display: "grid",
          placeItems: "center",
        }}
        aria-label={`زیاد کردن ${label}`}
        onClick={() => {
          onChange(Math.min(10_000, value + 1));
        }}
      >
        <Icon name="plus" />
      </button>
    </div>
  );
}

function ProblemReportModal({ dispatch, onClose }: { dispatch: DriverDispatchRow; onClose: () => void }) {
  const [kind, setKind] = useState<DeliveryProblemKind>("late");
  const [unitId, setUnitId] = useState(dispatch.stops[0]?.unitId ?? "");
  const [note, setNote] = useState("");

  const submit = useAction(async () => {
    await distributionApi.driverRecordProblem({
      businessDate: dispatch.businessDate,
      mealId: dispatch.mealId,
      unitId,
      dispatchId: dispatch.id,
      kind,
      note: note.trim(),
    });
    onClose();
  }, [["distribution", "driver"]]);

  return (
    <Panel
      title="گزارش مشکل در مسیر توزیع"
      actions={
        <Button variant="ghost" onClick={onClose}>
          بستن
        </Button>
      }
    >
      <form
        className="stack"
        style={{ gap: "var(--s3)" }}
        onSubmit={(e) => {
          e.preventDefault();
          submit.run(undefined);
        }}
      >
        <div className="field">
          <label htmlFor="prob-kind">نوع مشکل</label>
          <select
            id="prob-kind"
            className="input"
            value={kind}
            onChange={(e) => {
              setKind(e.target.value as DeliveryProblemKind);
            }}
            style={{ minHeight: 48, fontSize: 15 }}
          >
            <option value="late">تأخیر در رسیدن</option>
            <option value="shortage">کسری اقلام</option>
            <option value="quality">مشکل کیفی غذا</option>
            <option value="wrong-container">ظرف نامناسب / مغایر</option>
            <option value="other">سایر موارد</option>
          </select>
        </div>

        <div className="field">
          <label htmlFor="prob-unit">واحد مربوطه</label>
          <select
            id="prob-unit"
            className="input"
            value={unitId}
            onChange={(e) => {
              setUnitId(e.target.value);
            }}
            style={{ minHeight: 48, fontSize: 15 }}
          >
            {dispatch.stops.map((s) => (
              <option key={s.unitId} value={s.unitId}>
                {s.unitName}
              </option>
            ))}
          </select>
        </div>

        <Field
          id="prob-desc"
          label="توضیحات مشکل"
          value={note}
          onChange={(e) => {
            setNote(e.target.value);
          }}
          placeholder="شرح وضعیت و اقدامات لازم..."
          required
        />

        <ErrorLine error={submit.error} />

        <div className="row" style={{ gap: "var(--s2)", marginTop: "var(--s2)" }}>
          <Button variant="ghost" onClick={onClose} disabled={submit.busy} style={{ minHeight: 48 }}>
            انصراف
          </Button>
          <button
            type="submit"
            className="btn btn-primary btn-block"
            disabled={submit.busy || !note.trim()}
            style={{ minHeight: 48, fontSize: 16, fontWeight: 700 }}
          >
            {submit.busy ? "در حال ارسال..." : "ارسال گزارش مشکل"}
          </button>
        </div>
      </form>
    </Panel>
  );
}
